/**
 * Transform functions which takes in data as the input & return UI Config
 * Transform may be defined for a Question or an Answer as ultimately
 * the UI for both of them is represented by type 'RichComponentType[]'
 * Check './README.md' to know more.
 */

import { AcceptableResponse, APP_DOES_NOT_SUPPORT_DR, NOT_APPLICABLE, STORED_EXTERNAL_TO_ATLASSIAN, STORED_IN_ATLASSIAN_AND_DR_NOT_SUPPORTED, STORED_IN_ATLASSIAN_AND_DR_SUPPORTED } from '@atlassian/mpac-api-client';
import { AppDeployment } from '@atlassian/mpac-types';
import { MarketplaceProgramStatus } from '@atlassian/mpac-types/graphql-gateway-types';
import { pluralize } from '@atlassian/mpac-utils';
import { arrayToPoint } from '../../CustomerComponents/BulletedPoints/BulletedPoints';
const isResponseNotProvided = value => typeof value === 'undefined' || value === null;
const notApplicableResponses = {
  NO_END_USER_DATA: [{
    type: 'text',
    text: 'Not applicable. App does not process or store End-User Data outside of Atlassian products and services.'
  }],
  NO_END_USER_DATA_PROCESSED: [{
    type: 'text',
    text: 'Not applicable. App does not process End-User Data outside of Atlassian products and services.'
  }],
  NO_END_USER_DATA_STORED_EXTERNALLY: [{
    type: 'text',
    text: 'Not applicable. App does not store End-User Data outside of Atlassian products and services.'
  }],
  STORED_IN_ATLASSIAN_AND_DR_NOT_SUPPORTED: [{
    type: 'rich-text',
    items: [{
      type: 'text',
      text: "Not applicable. App stores End-User Data exclusively within Atlassian products and services, which don't support data residency options yet due to Atlassian\u2019s current roadmap."
    }]
  }],
  APP_DOES_NOT_SUPPORT_DR: [{
    type: 'text',
    text: "Not applicable. App does not support data residency options."
  }],
  NO_END_USER_DATA_STORED: [{
    type: 'text',
    text: "Not applicable. App does not store End-User Data."
  }]
};
const yesNoNull = bool => {
  if (isResponseNotProvided(bool)) {
    return null;
  }
  return bool ? [{
    type: 'text',
    text: 'Yes'
  }] : [{
    type: 'text',
    text: 'No'
  }];
};

// eslint-disable-next-line complexity
export const endUserDataStoresAndProcessAnswer = _ref => {
  let {
    dataAccessAndStorage
  } = _ref;
  const appProcessEUDOutsideAtlassian = dataAccessAndStorage === null || dataAccessAndStorage === void 0 ? void 0 : dataAccessAndStorage.appProcessEUDOutsideAtlassian;
  const typesOfDataAccessed = dataAccessAndStorage === null || dataAccessAndStorage === void 0 ? void 0 : dataAccessAndStorage.typesOfDataAccessed;
  const appStoresEUDOutsideAtlassian = dataAccessAndStorage === null || dataAccessAndStorage === void 0 ? void 0 : dataAccessAndStorage.appStoresEUDOutsideAtlassian;
  const typesOfDataStored = dataAccessAndStorage === null || dataAccessAndStorage === void 0 ? void 0 : dataAccessAndStorage.typesOfDataStored;
  const isSameDataProcessedAndStored = dataAccessAndStorage === null || dataAccessAndStorage === void 0 ? void 0 : dataAccessAndStorage.isSameDataProcessedAndStored;
  const isProcessResponseNotProvided = isResponseNotProvided(appProcessEUDOutsideAtlassian);
  const isStoredResponseNotProvided = isResponseNotProvided(appStoresEUDOutsideAtlassian);
  if (isProcessResponseNotProvided && isStoredResponseNotProvided) {
    return null;
  }
  if (!appProcessEUDOutsideAtlassian && !appStoresEUDOutsideAtlassian) {
    return [{
      type: 'text',
      text: 'The app does not process or store End-User Data outside of Atlassian products and services.'
    }];
  }
  if (typesOfDataAccessed && !typesOfDataStored) {
    return [{
      type: 'bullet-points-list',
      bulletedList: [{
        label: "The app processes ".concat(isStoredResponseNotProvided ? '' : 'but does not store ', "the following types of End-User Data:"),
        points: arrayToPoint(typesOfDataAccessed)
      }]
    }];
  }
  if (!typesOfDataAccessed && typesOfDataStored) {
    return [{
      type: 'bullet-points-list',
      bulletedList: [{
        label: "The app ".concat(isProcessResponseNotProvided ? '' : 'does not process but ', "stores the following types of End-User Data:"),
        points: arrayToPoint(typesOfDataStored)
      }]
    }];
  }
  if (typesOfDataAccessed && typesOfDataStored) {
    if (isSameDataProcessedAndStored) {
      return [{
        type: 'bullet-points-list',
        bulletedList: [{
          label: 'The app processes and stores the following types of End-User Data:',
          points: arrayToPoint(typesOfDataStored)
        }]
      }];
    }
    return [{
      type: 'bullet-points-list',
      bulletedList: [{
        label: 'The app processes the following types of End-User Data:',
        points: arrayToPoint(typesOfDataAccessed)
      }, {
        label: 'The app stores the following types of End-User Data:',
        points: arrayToPoint(typesOfDataStored)
      }]
    }];
  }
  return null;
};
const handleEndUserDataNotApplicable = dataAccessAndStorage => {
  const appProcessEUDOutsideAtlassian = dataAccessAndStorage === null || dataAccessAndStorage === void 0 ? void 0 : dataAccessAndStorage.appProcessEUDOutsideAtlassian;
  const appStoresEUDOutsideAtlassian = dataAccessAndStorage === null || dataAccessAndStorage === void 0 ? void 0 : dataAccessAndStorage.appStoresEUDOutsideAtlassian;
  if (isResponseNotProvided(appProcessEUDOutsideAtlassian) && isResponseNotProvided(appStoresEUDOutsideAtlassian)) {
    return null;
  } else if (!appProcessEUDOutsideAtlassian && isResponseNotProvided(appStoresEUDOutsideAtlassian)) {
    return notApplicableResponses.NO_END_USER_DATA_PROCESSED;
  } else if (isResponseNotProvided(appProcessEUDOutsideAtlassian) && !appStoresEUDOutsideAtlassian) {
    return notApplicableResponses.NO_END_USER_DATA_STORED_EXTERNALLY;
  } else if (!appProcessEUDOutsideAtlassian && !appStoresEUDOutsideAtlassian) {
    return notApplicableResponses.NO_END_USER_DATA;
  }
  return null;
};
const handleEndUserDataStored = dataAccessAndStorage => {
  const appStoresEUDOutsideAtlassian = dataAccessAndStorage === null || dataAccessAndStorage === void 0 ? void 0 : dataAccessAndStorage.appStoresEUDOutsideAtlassian;
  if (isResponseNotProvided(appStoresEUDOutsideAtlassian)) {
    return null;
  } else if (!appStoresEUDOutsideAtlassian) {
    return notApplicableResponses.NO_END_USER_DATA_STORED_EXTERNALLY;
  }
  return null;
};
export const logEndUserData = _ref2 => {
  let {
    logDetails
  } = _ref2;
  return yesNoNull(logDetails === null || logDetails === void 0 ? void 0 : logDetails.logEndUserData);
};
export const logEndUserDataOutsideAtlassian = _ref3 => {
  let {
    logDetails
  } = _ref3;
  return yesNoNull(logDetails === null || logDetails === void 0 ? void 0 : logDetails.logProcessAndOrStoreEUDOutsideAtlassian);
};
export const endUserDataSharedWithThirdPartyEntities = _ref4 => {
  let {
    thirdPartyInformation,
    dataAccessAndStorage
  } = _ref4;
  const isEndUserDataShared = thirdPartyInformation === null || thirdPartyInformation === void 0 ? void 0 : thirdPartyInformation.isEndUserDataShared;
  if (isResponseNotProvided(isEndUserDataShared)) {
    return handleEndUserDataStored(dataAccessAndStorage);
  }
  if (!isEndUserDataShared) {
    return [{
      type: 'text',
      text: 'Not applicable. The app does not share End-User Data with third parties.'
    }];
  }
  if (thirdPartyInformation.thirdPartyDetails) {
    return [{
      type: 'bullet-points-list',
      bulletedList: thirdPartyInformation.thirdPartyDetails.map(_ref5 => {
        let {
          name,
          link,
          purpose,
          thirdPartyCountries
        } = _ref5;
        return {
          label: name,
          points: [{
            text: "Link: ".concat(link)
          }, {
            label: 'Location: ',
            isoCodes: thirdPartyCountries
          }, {
            text: "Purpose: ".concat(purpose)
          }]
        };
      })
    }];
  }
  if (thirdPartyInformation.dataSubProcessors) {
    return [{
      type: 'link-with-icon',
      href: thirdPartyInformation.dataSubProcessors,
      text: 'Sub-processor list'
    }];
  }
  return null;
};
export const logSharedWithThirdParty = _ref6 => {
  let {
    logDetails
  } = _ref6;
  return yesNoNull(logDetails === null || logDetails === void 0 ? void 0 : logDetails.logEUDShareWithThirdParty);
};
export const logIntegralPartOfFunctionality = _ref7 => {
  let {
    logDetails
  } = _ref7;
  return yesNoNull(logDetails === null || logDetails === void 0 ? void 0 : logDetails.logsIntegralForAppFunctionality);
};
export const dataResidencyCommonHandling = isDataResidencySupported => {
  if (isDataResidencySupported === APP_DOES_NOT_SUPPORT_DR) {
    return notApplicableResponses.APP_DOES_NOT_SUPPORT_DR;
  }
  if (isDataResidencySupported === STORED_IN_ATLASSIAN_AND_DR_NOT_SUPPORTED) {
    return notApplicableResponses.STORED_IN_ATLASSIAN_AND_DR_NOT_SUPPORTED;
  }
  if (isDataResidencySupported === NOT_APPLICABLE) {
    return notApplicableResponses.NO_END_USER_DATA_STORED;
  }
  return null;
};
export const dataResidencyOptions = _ref8 => {
  let {
    dataResidency,
    security
  } = _ref8;
  const isDataResidencySupported = dataResidency === null || dataResidency === void 0 ? void 0 : dataResidency.isDataResidencySupported;
  const countriesWhereEndUserDataStored = dataResidency === null || dataResidency === void 0 ? void 0 : dataResidency.countriesWhereEndUserDataStored;
  const contact = security === null || security === void 0 ? void 0 : security.securityContact;
  if (isResponseNotProvided(isDataResidencySupported)) {
    return null;
  }
  if (isDataResidencySupported === STORED_EXTERNAL_TO_ATLASSIAN && countriesWhereEndUserDataStored) {
    const uiConfig = [{
      type: 'text',
      text: 'App supports data residency and stores End-User Data independently outside of Atlassian products and services in the following locations:'
    }, {
      type: 'countries',
      isoCodes: countriesWhereEndUserDataStored
    }];
    if (contact) {
      uiConfig.push({
        type: 'rich-text',
        items: [{
          type: 'text',
          text: 'More information on the app’s data residency policy can be found in app’s product documentation or please contact the respective app partner at '
        }, {
          type: 'email',
          text: contact,
          mailId: contact
        }]
      });
    }
    return uiConfig;
  }
  if (isDataResidencySupported === STORED_IN_ATLASSIAN_AND_DR_SUPPORTED) {
    return [{
      type: 'rich-text',
      items: [{
        type: 'text',
        text: 'Yes. App stores End-User Data exclusively within Atlassian products and services which support data residency options, as outlined '
      }, {
        type: 'link-with-icon',
        text: ' here.',
        href: 'https://support.atlassian.com/security-and-access-policies/docs/understand-data-residency/'
      }]
    }];
  }
  return dataResidencyCommonHandling(isDataResidencySupported);
};
const storedInAtlassianAndDrSupportedConfig = [{
  type: 'rich-text',
  items: [{
    type: 'text',
    text: 'Yes. App stores End-User Data exclusively within Atlassian products and services which support data residency options, as outlined '
  }, {
    type: 'link-with-icon',
    text: ' here.',
    href: 'https://support.atlassian.com/security-and-access-policies/docs/understand-data-residency/'
  }]
}];
export const dataResidencyInScopeEndUserDataV2 = _ref9 => {
  let {
    dataResidency
  } = _ref9;
  const isDataResidencySupported = dataResidency === null || dataResidency === void 0 ? void 0 : dataResidency.isDataResidencySupported;
  const inScopeDataTypes = dataResidency === null || dataResidency === void 0 ? void 0 : dataResidency.inScopeDataTypes;
  if ((isDataResidencySupported === STORED_EXTERNAL_TO_ATLASSIAN || isDataResidencySupported === STORED_IN_ATLASSIAN_AND_DR_SUPPORTED) && inScopeDataTypes) {
    return [{
      type: 'bullet-points-list',
      bulletedList: [{
        label: 'App stores the following types of in-scope end-user data:',
        points: arrayToPoint(inScopeDataTypes)
      }]
    }];
  }
  return dataResidencyCommonHandling(isDataResidencySupported);
};
export const dataResidencyOutOfScopeEndUserData = _ref10 => {
  let {
    dataResidency
  } = _ref10;
  const isDataResidencySupported = dataResidency === null || dataResidency === void 0 ? void 0 : dataResidency.isDataResidencySupported;
  const outOfScopeDataTypes = dataResidency === null || dataResidency === void 0 ? void 0 : dataResidency.outOfScopeDataTypes;
  if ((isDataResidencySupported === STORED_EXTERNAL_TO_ATLASSIAN || isDataResidencySupported === STORED_IN_ATLASSIAN_AND_DR_SUPPORTED) && outOfScopeDataTypes) {
    return [{
      type: 'bullet-points-list',
      bulletedList: [{
        label: 'App stores the following types of out-of-scope end-user data:',
        points: arrayToPoint(outOfScopeDataTypes)
      }]
    }];
  }
  return dataResidencyCommonHandling(isDataResidencySupported);
};
export const dataResidencyInScopeEndUserData = _ref11 => {
  let {
    dataResidency
  } = _ref11;
  const isDataResidencySupported = dataResidency === null || dataResidency === void 0 ? void 0 : dataResidency.isDataResidencySupported;
  const inScopeDataTypes = dataResidency === null || dataResidency === void 0 ? void 0 : dataResidency.inScopeDataTypes;
  if (isDataResidencySupported === STORED_EXTERNAL_TO_ATLASSIAN && inScopeDataTypes) {
    return [{
      type: 'bullet-points-list',
      bulletedList: [{
        label: 'App stores the following types of in-scope end-user data:',
        points: arrayToPoint(inScopeDataTypes)
      }]
    }];
  }
  if (isDataResidencySupported === STORED_IN_ATLASSIAN_AND_DR_SUPPORTED) {
    return storedInAtlassianAndDrSupportedConfig;
  }
  return dataResidencyCommonHandling(isDataResidencySupported);
};
export const dataResidencyRealmMigration = _ref12 => {
  let {
    dataResidency
  } = _ref12;
  const isDataResidencySupported = dataResidency === null || dataResidency === void 0 ? void 0 : dataResidency.isDataResidencySupported;
  const realmMigrationSupported = dataResidency === null || dataResidency === void 0 ? void 0 : dataResidency.realmMigrationSupported;
  if (isResponseNotProvided(realmMigrationSupported)) {
    if (isDataResidencySupported === STORED_IN_ATLASSIAN_AND_DR_SUPPORTED) {
      return storedInAtlassianAndDrSupportedConfig;
    }
    return dataResidencyCommonHandling(isDataResidencySupported);
  }
  return yesNoNull(realmMigrationSupported);
};

// eslint-disable-next-line complexity
const handleDataRetentionTransformDependency = (dataAccessAndStorage, dataResidency) => {
  const appStoresEUDOutsideAtlassian = dataAccessAndStorage === null || dataAccessAndStorage === void 0 ? void 0 : dataAccessAndStorage.appStoresEUDOutsideAtlassian;
  const isDataResidencySupported = dataResidency === null || dataResidency === void 0 ? void 0 : dataResidency.isDataResidencySupported;
  const isDataResidencySupportedBool = isDataResidencySupported === STORED_EXTERNAL_TO_ATLASSIAN;
  switch (true) {
    case isResponseNotProvided(appStoresEUDOutsideAtlassian) && isResponseNotProvided(isDataResidencySupported):
      return null;
    case !appStoresEUDOutsideAtlassian && isResponseNotProvided(isDataResidencySupported):
      return notApplicableResponses.NO_END_USER_DATA_STORED_EXTERNALLY;

    // Same copy for both appStoresEUDOutsideAtlassian is not responded & false
    // while DR is supported through atlassian
    case !appStoresEUDOutsideAtlassian && isDataResidencySupported === STORED_IN_ATLASSIAN_AND_DR_SUPPORTED:
      return storedInAtlassianAndDrSupportedConfig;
    case isResponseNotProvided(appStoresEUDOutsideAtlassian) && isDataResidencySupported === APP_DOES_NOT_SUPPORT_DR:
      return notApplicableResponses.APP_DOES_NOT_SUPPORT_DR;
    case isResponseNotProvided(appStoresEUDOutsideAtlassian) && isDataResidencySupported === STORED_IN_ATLASSIAN_AND_DR_NOT_SUPPORTED:
      return notApplicableResponses.STORED_IN_ATLASSIAN_AND_DR_NOT_SUPPORTED;
    case isDataResidencySupported === NOT_APPLICABLE:
      return notApplicableResponses.NO_END_USER_DATA_STORED;
    case !appStoresEUDOutsideAtlassian && !isDataResidencySupportedBool:
      return [{
        type: 'text',
        text: 'Not applicable, App neither stores End-User data nor supports data residency options.'
      }];
    default:
      break;
  }
  return null;
};

// eslint-disable-next-line complexity
export const dataRetentionTransform = _ref13 => {
  let {
    dataRetention,
    dataAccessAndStorage,
    dataResidency
  } = _ref13;
  const isDataRetentionSupported = dataRetention === null || dataRetention === void 0 ? void 0 : dataRetention.isDataRetentionSupported;
  const retentionDurationInDays = dataRetention === null || dataRetention === void 0 ? void 0 : dataRetention.retentionDurationInDays;
  const isRetentionDurationIndefinite = dataRetention === null || dataRetention === void 0 ? void 0 : dataRetention.isRetentionDurationIndefinite;
  const retentionReason = dataRetention === null || dataRetention === void 0 ? void 0 : dataRetention.retentionReason;
  if (isResponseNotProvided(isDataRetentionSupported)) {
    return handleDataRetentionTransformDependency(dataAccessAndStorage, dataResidency);
  }
  if (isRetentionDurationIndefinite === true) {
    return [{
      type: 'text',
      text: 'End-User Data is stored indefinitely after app uninstallation'
    }];
  }
  if (isDataRetentionSupported && retentionDurationInDays) {
    const message = (prefix, number) => "".concat(prefix, " storage period post un-install: ").concat(number, " ").concat(pluralize('day', number));
    const uiConfig = [{
      type: 'text',
      text: message('Minimum', retentionDurationInDays.min)
    }, {
      type: 'text',
      text: message('Maximum', retentionDurationInDays.max)
    }];
    if (retentionReason) {
      uiConfig.push({
        type: 'text',
        text: "Reason for storage: ".concat(retentionReason)
      });
    }
    return uiConfig;
  }
  return yesNoNull(isDataRetentionSupported);
};
export const customRetentionPeriod = _ref14 => {
  let {
    dataRetention,
    dataAccessAndStorage
  } = _ref14;
  const isCustomRetentionPeriodAllowed = dataRetention === null || dataRetention === void 0 ? void 0 : dataRetention.isCustomRetentionPeriodAllowed;
  if (isResponseNotProvided(isCustomRetentionPeriodAllowed)) {
    return handleEndUserDataStored(dataAccessAndStorage);
  }
  return yesNoNull(isCustomRetentionPeriodAllowed);
};
export const privacyEnhancingTechniques = _ref15 => {
  var _privacy$privacyEnhan, _privacy$privacyEnhan2;
  let {
    privacy,
    dataAccessAndStorage
  } = _ref15;
  const arePrivacyEnhancingTechniquesSupported = privacy === null || privacy === void 0 ? void 0 : (_privacy$privacyEnhan = privacy.privacyEnhancingTechniques) === null || _privacy$privacyEnhan === void 0 ? void 0 : _privacy$privacyEnhan.arePrivacyEnhancingTechniquesSupported;
  const privacyEnhancingTechniquesSupported = privacy === null || privacy === void 0 ? void 0 : (_privacy$privacyEnhan2 = privacy.privacyEnhancingTechniques) === null || _privacy$privacyEnhan2 === void 0 ? void 0 : _privacy$privacyEnhan2.privacyEnhancingTechniquesSupported;
  if (isResponseNotProvided(arePrivacyEnhancingTechniquesSupported)) {
    return handleEndUserDataNotApplicable(dataAccessAndStorage);
  }
  if (privacyEnhancingTechniquesSupported) {
    return [{
      type: 'bullet-points-list',
      bulletedList: [{
        label: 'Yes',
        points: arrayToPoint(privacyEnhancingTechniquesSupported)
      }]
    }];
  }
  return yesNoNull(arePrivacyEnhancingTechniquesSupported);
};
const commonPrivacyTransform = _ref16 => {
  let {
    bool,
    arr,
    notApplicableText,
    arrLabel
  } = _ref16;
  if (isResponseNotProvided(bool)) {
    return null;
  }
  if (bool === AcceptableResponse.NOT_APPLICABLE) {
    return [{
      type: 'text',
      text: notApplicableText
    }];
  }
  if (arr) {
    return [{
      type: 'bullet-points-list',
      bulletedList: [{
        label: arrLabel,
        points: arrayToPoint(arr)
      }]
    }];
  }
  return yesNoNull(false);
};
export const gdprDataController = _ref17 => {
  var _privacy$gdpr, _privacy$gdpr$dataCon, _privacy$gdpr2, _privacy$gdpr2$dataCo;
  let {
    privacy
  } = _ref17;
  const isAppDataController = privacy === null || privacy === void 0 ? void 0 : (_privacy$gdpr = privacy.gdpr) === null || _privacy$gdpr === void 0 ? void 0 : (_privacy$gdpr$dataCon = _privacy$gdpr.dataController) === null || _privacy$gdpr$dataCon === void 0 ? void 0 : _privacy$gdpr$dataCon.isAppDataController;
  const endUserDataTypes = privacy === null || privacy === void 0 ? void 0 : (_privacy$gdpr2 = privacy.gdpr) === null || _privacy$gdpr2 === void 0 ? void 0 : (_privacy$gdpr2$dataCo = _privacy$gdpr2.dataController) === null || _privacy$gdpr2$dataCo === void 0 ? void 0 : _privacy$gdpr2$dataCo.endUserDataTypes;
  return commonPrivacyTransform({
    arr: endUserDataTypes,
    bool: isAppDataController,
    arrLabel: "Yes. Company/Organization is a 'data controller' for the following types of End-User Data:",
    notApplicableText: 'Not applicable. Company/Organization is not subject to the GDPR.'
  });
};
export const gdprDataProcessor = _ref18 => {
  var _privacy$gdpr3, _privacy$gdpr3$dataPr, _privacy$gdpr4, _privacy$gdpr4$dataPr;
  let {
    privacy
  } = _ref18;
  const isAppDataProcessor = privacy === null || privacy === void 0 ? void 0 : (_privacy$gdpr3 = privacy.gdpr) === null || _privacy$gdpr3 === void 0 ? void 0 : (_privacy$gdpr3$dataPr = _privacy$gdpr3.dataProcessor) === null || _privacy$gdpr3$dataPr === void 0 ? void 0 : _privacy$gdpr3$dataPr.isAppDataProcessor;
  const endUserDataTypes = privacy === null || privacy === void 0 ? void 0 : (_privacy$gdpr4 = privacy.gdpr) === null || _privacy$gdpr4 === void 0 ? void 0 : (_privacy$gdpr4$dataPr = _privacy$gdpr4.dataProcessor) === null || _privacy$gdpr4$dataPr === void 0 ? void 0 : _privacy$gdpr4$dataPr.endUserDataTypes;
  return commonPrivacyTransform({
    arr: endUserDataTypes,
    bool: isAppDataProcessor,
    arrLabel: "Yes. Company/Organization is a 'data processor' for the following types of End-User Data:",
    notApplicableText: 'Not applicable. Company/Organization is not subject to the GDPR.'
  });
};
export const ccpaBusiness = _ref19 => {
  var _privacy$ccpa, _privacy$ccpa$busines, _privacy$ccpa2, _privacy$ccpa2$busine;
  let {
    privacy
  } = _ref19;
  const isAppBusiness = privacy === null || privacy === void 0 ? void 0 : (_privacy$ccpa = privacy.ccpa) === null || _privacy$ccpa === void 0 ? void 0 : (_privacy$ccpa$busines = _privacy$ccpa.business) === null || _privacy$ccpa$busines === void 0 ? void 0 : _privacy$ccpa$busines.isAppBusiness;
  const endUserDataTypes = privacy === null || privacy === void 0 ? void 0 : (_privacy$ccpa2 = privacy.ccpa) === null || _privacy$ccpa2 === void 0 ? void 0 : (_privacy$ccpa2$busine = _privacy$ccpa2.business) === null || _privacy$ccpa2$busine === void 0 ? void 0 : _privacy$ccpa2$busine.endUserDataTypes;
  return commonPrivacyTransform({
    arr: endUserDataTypes,
    bool: isAppBusiness,
    arrLabel: "Yes. Company/Organization is a 'business' for the following types of End-User Data:",
    notApplicableText: 'Not applicable. Company/Organization is not subject to the CCPA.'
  });
};
export const ccpaServiceProvider = _ref20 => {
  var _privacy$ccpa3, _privacy$ccpa3$servic, _privacy$ccpa4, _privacy$ccpa4$servic;
  let {
    privacy
  } = _ref20;
  const isAppServiceProvider = privacy === null || privacy === void 0 ? void 0 : (_privacy$ccpa3 = privacy.ccpa) === null || _privacy$ccpa3 === void 0 ? void 0 : (_privacy$ccpa3$servic = _privacy$ccpa3.serviceProvider) === null || _privacy$ccpa3$servic === void 0 ? void 0 : _privacy$ccpa3$servic.isAppServiceProvider;
  const endUserDataTypes = privacy === null || privacy === void 0 ? void 0 : (_privacy$ccpa4 = privacy.ccpa) === null || _privacy$ccpa4 === void 0 ? void 0 : (_privacy$ccpa4$servic = _privacy$ccpa4.serviceProvider) === null || _privacy$ccpa4$servic === void 0 ? void 0 : _privacy$ccpa4$servic.endUserDataTypes;
  return commonPrivacyTransform({
    arr: endUserDataTypes,
    bool: isAppServiceProvider,
    arrLabel: "Yes. Company/Organization is a 'service provider' for the following types of End-User Data:",
    notApplicableText: 'Not applicable. Company/Organization is not subject to the CCPA.'
  });
};
export const dataProcessingAgreement = _ref21 => {
  var _privacy$gdpr5, _privacy$gdpr5$dataPr, _privacy$ccpa5, _privacy$ccpa5$servic, _privacy$dataProcessi, _privacy$dataProcessi2;
  let {
    privacy
  } = _ref21;
  const isAppDataProcessor = privacy === null || privacy === void 0 ? void 0 : (_privacy$gdpr5 = privacy.gdpr) === null || _privacy$gdpr5 === void 0 ? void 0 : (_privacy$gdpr5$dataPr = _privacy$gdpr5.dataProcessor) === null || _privacy$gdpr5$dataPr === void 0 ? void 0 : _privacy$gdpr5$dataPr.isAppDataProcessor;
  const isAppServiceProvider = privacy === null || privacy === void 0 ? void 0 : (_privacy$ccpa5 = privacy.ccpa) === null || _privacy$ccpa5 === void 0 ? void 0 : (_privacy$ccpa5$servic = _privacy$ccpa5.serviceProvider) === null || _privacy$ccpa5$servic === void 0 ? void 0 : _privacy$ccpa5$servic.isAppServiceProvider;
  const isDPASupported = privacy === null || privacy === void 0 ? void 0 : (_privacy$dataProcessi = privacy.dataProcessingAgreement) === null || _privacy$dataProcessi === void 0 ? void 0 : _privacy$dataProcessi.isDPASupported;
  const link = privacy === null || privacy === void 0 ? void 0 : (_privacy$dataProcessi2 = privacy.dataProcessingAgreement) === null || _privacy$dataProcessi2 === void 0 ? void 0 : _privacy$dataProcessi2.link;
  if (isResponseNotProvided(isDPASupported)) {
    if (isAppDataProcessor === AcceptableResponse.NOT_APPLICABLE && isAppServiceProvider === AcceptableResponse.NOT_APPLICABLE) {
      return null;
    } else if (isAppDataProcessor === AcceptableResponse.FALSE && isAppServiceProvider === AcceptableResponse.NOT_APPLICABLE) {
      return [{
        type: 'text',
        text: 'Not applicable. Company/Organization is not a ‘data processor’ under GDPR.'
      }];
    } else if (isAppDataProcessor === AcceptableResponse.NOT_APPLICABLE && isAppServiceProvider === AcceptableResponse.FALSE) {
      return [{
        type: 'text',
        text: 'Not applicable. Company/Organization is not a ‘service provider’ under CCPA.'
      }];
    } else if (isAppDataProcessor === AcceptableResponse.FALSE && isAppServiceProvider === AcceptableResponse.FALSE) {
      return [{
        type: 'text',
        text: 'Not applicable. Company/Organization is neither a ‘data processor’ under GDPR nor a ‘service provider’ under CCPA.'
      }];
    }
  }
  if (link) {
    return [{
      type: 'rich-text',
      items: [{
        type: 'text',
        text: 'Yes, '
      }, {
        type: 'link-with-icon',
        href: link,
        text: 'DPA'
      }]
    }];
  }
  return yesNoNull(false);
};
export const gdprDataTransfer = _ref22 => {
  var _privacy$gdpr6, _privacy$gdpr6$dataTr, _privacy$gdpr7, _privacy$gdpr7$dataTr;
  let {
    privacy,
    dataAccessAndStorage
  } = _ref22;
  const isEndUserDataTransferredOutsideEEA = privacy === null || privacy === void 0 ? void 0 : (_privacy$gdpr6 = privacy.gdpr) === null || _privacy$gdpr6 === void 0 ? void 0 : (_privacy$gdpr6$dataTr = _privacy$gdpr6.dataTransfer) === null || _privacy$gdpr6$dataTr === void 0 ? void 0 : _privacy$gdpr6$dataTr.isEndUserDataTransferredOutsideEEA;
  const transferComplianceMechanismsAdheredDetails = privacy === null || privacy === void 0 ? void 0 : (_privacy$gdpr7 = privacy.gdpr) === null || _privacy$gdpr7 === void 0 ? void 0 : (_privacy$gdpr7$dataTr = _privacy$gdpr7.dataTransfer) === null || _privacy$gdpr7$dataTr === void 0 ? void 0 : _privacy$gdpr7$dataTr.transferComplianceMechanismsAdheredDetails;
  if (isResponseNotProvided(isEndUserDataTransferredOutsideEEA)) {
    return handleEndUserDataNotApplicable(dataAccessAndStorage);
  }
  if (!isEndUserDataTransferredOutsideEEA) {
    return [{
      type: 'text',
      text: "No. App does not transfer EEA residents' data outside of the EEA."
    }];
  }
  if (transferComplianceMechanismsAdheredDetails) {
    return [{
      type: 'text',
      text: "Yes, ".concat(transferComplianceMechanismsAdheredDetails)
    }];
  }
  return yesNoNull(false);
};
export const bugBountyProgramTransform = _ref23 => {
  var _programs$bugBountyPa, _programs$bugBountyPa2;
  let {
    programs
  } = _ref23;
  const status = programs === null || programs === void 0 ? void 0 : (_programs$bugBountyPa = programs.bugBountyParticipant) === null || _programs$bugBountyPa === void 0 ? void 0 : (_programs$bugBountyPa2 = _programs$bugBountyPa.cloud) === null || _programs$bugBountyPa2 === void 0 ? void 0 : _programs$bugBountyPa2.status;
  if (status === MarketplaceProgramStatus.Approved) {
    return [{
      type: 'text',
      text: 'Yes. App is a participant in Marketplace Security Bug Bounty Program.'
    }];
  } else if (status === MarketplaceProgramStatus.Rejected) {
    return [{
      type: 'text',
      text: 'Not applicable. App had applied for Marketplace Security Bug Bounty Program and got rejected.'
    }];
  } else if (status === MarketplaceProgramStatus.Applied) {
    return [{
      type: 'text',
      text: 'Not applicable. App has applied for Marketplace Security Bug Bounty Program and awaits status.'
    }];
  } else if (status === MarketplaceProgramStatus.NotAParticipant) {
    return [{
      type: 'text',
      text: 'No. App is not a participant in Marketplace Security Bug Bounty Program.'
    }];
  }
  return yesNoNull(false);
};
export const securityContact = _ref24 => {
  let {
    security
  } = _ref24;
  const contact = security === null || security === void 0 ? void 0 : security.securityContact;
  if (!contact) {
    return null;
  }
  return [{
    type: 'rich-text',
    items: [{
      type: 'text',
      text: 'App’s e-mail contact: '
    }, {
      type: 'email',
      text: contact,
      mailId: contact
    }]
  }];
};
export const securityPolicyLink = _ref25 => {
  let {
    security,
    byAtlassian
  } = _ref25;
  if (!(security !== null && security !== void 0 && security.publicSecurityPoliciesLink)) {
    return null;
  }
  return [{
    type: 'link-with-icon',
    href: security.publicSecurityPoliciesLink,
    text: byAtlassian ? 'Atlassian security policy' : 'Partner security policy'
  }];
};
export const securityCaiqLite = _ref26 => {
  var _security$caiq, _security$caiq2;
  let {
    security
  } = _ref26;
  const isCAIQCompleted = security === null || security === void 0 ? void 0 : (_security$caiq = security.caiq) === null || _security$caiq === void 0 ? void 0 : _security$caiq.isCAIQCompleted;
  const CAIQLiteLink = security === null || security === void 0 ? void 0 : (_security$caiq2 = security.caiq) === null || _security$caiq2 === void 0 ? void 0 : _security$caiq2.CAIQLiteLink;
  if (isResponseNotProvided(isCAIQCompleted)) {
    return null;
  }
  if (!isCAIQCompleted || !CAIQLiteLink) {
    return [{
      type: 'text',
      text: 'CAIQ Lite Questionnaire has not been submitted'
    }];
  }
  return [{
    type: 'rich-text',
    items: [{
      type: 'text',
      text: 'Yes, '
    }, {
      type: 'link-with-icon',
      text: 'CAIQ Lite response',
      href: CAIQLiteLink
    }]
  }];
};
export const securityDiskEncryption = _ref27 => {
  let {
    security,
    dataAccessAndStorage
  } = _ref27;
  const isDiskEncryptionSupported = security === null || security === void 0 ? void 0 : security.isDiskEncryptionSupported;
  if (isResponseNotProvided(isDiskEncryptionSupported)) {
    return handleEndUserDataStored(dataAccessAndStorage);
  }
  return yesNoNull(isDiskEncryptionSupported);
};
export const compliantCertifications = _ref28 => {
  let {
    security
  } = _ref28;
  const hasCompliantCertifications = security === null || security === void 0 ? void 0 : security.hasCompliantCertifications;
  const certifications = security === null || security === void 0 ? void 0 : security.compliantCertifications;
  if (isResponseNotProvided(hasCompliantCertifications)) {
    return null;
  }
  return hasCompliantCertifications && certifications ? [{
    type: 'bullet-points',
    points: arrayToPoint(certifications)
  }] : [{
    type: 'text',
    text: 'None.'
  }];
};
export const privacyPolicy = _ref29 => {
  let {
    privacyPolicyUrl,
    byAtlassian
  } = _ref29;
  if (!privacyPolicyUrl) {
    return null;
  }
  if (byAtlassian) {
    return [{
      type: 'text',
      text: "Atlassian\u2019s privacy policy is applicable to the use of this app."
    }, {
      type: 'link-with-icon',
      text: 'Atlassian privacy policy',
      href: privacyPolicyUrl
    }];
  }
  return [{
    type: 'text',
    text: "Atlassian's privacy policy is not applicable to the use of this app. Please refer to the privacy policy provided by this app's partner."
  }, {
    type: 'link-with-icon',
    text: 'Partner privacy policy',
    href: privacyPolicyUrl
  }];
};
export const integrationPermisions = _ref30 => {
  let {
    appScopes
  } = _ref30;
  if ((appScopes === null || appScopes === void 0 ? void 0 : appScopes.length) > 0) {
    return [{
      type: 'bullet-points',
      points: arrayToPoint(appScopes.map(scope => scope.capability))
    }];
  }
  return [{
    type: 'text',
    text: 'None.'
  }];
};

// QUESTION TRANSFORMS BELOW

export const endUserDataLogQuestion = _ref31 => {
  let {
    deploymentType
  } = _ref31;
  if (deploymentType === AppDeployment.ECOSYSTEM_CLOUD) return [{
    type: 'text',
    text: 'End-User Data accessed in logs'
  }, {
    type: 'info-label-with-tooltip',
    text: 'Learn more about access to logs',
    content: [{
      type: 'text',
      text: 'Partners get access to app logs by default, which helps with troubleshooting issues. You can view the content of app logs or disable app log access any time.'
    }, {
      type: 'link-with-icon',
      text: 'Read more about accessing and disabling logs',
      href: 'https://support.atlassian.com/security-and-access-policies/docs/manage-your-users-third-party-apps/#Troubleshoot-an-app'
    }]
  }];else return [{
    type: 'text',
    text: 'End-User Data accessed in logs'
  }];
};